"use client";

import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fetchAutomationsCheckoutSession,
  updateAutomationsCheckoutPlan,
  type AutomationsCheckoutSessionResult,
} from "../../client/manager/automations";
import type { AutomationsCheckoutSurface } from "../../models/automations-checkout-return";
import {
  isSamePlan,
  type AutomationsPlanKey,
} from "../../models/automations-plans";
import {
  invalidateAutomations,
  removeAutomationsCheckoutSessions,
} from "./automationsInvalidation";
import { automationsKeys } from "./automationsQueryKeys";

/** Under Stripe's 24h default session expiry, so a reused session is still live. */
const SESSION_LIFETIME_MS = 60 * 60 * 1000;

export interface UseAutomationsCheckoutSessionOptions {
  artistId: string;
  selection: AutomationsPlanKey;
  returnTo: string;
  surface: AutomationsCheckoutSurface;
  enabled: boolean;
}

/** The open session: its secret and id, and the plan it holds (minted with, or last switched to). */
export interface AutomationsOpenCheckoutSession {
  clientSecret: string;
  sessionId: string;
  plan: AutomationsPlanKey;
}

export interface UseAutomationsCheckoutSessionResult {
  /** Null while it is being minted, or when there is none (a plan already held, a failure). */
  session: AutomationsOpenCheckoutSession | null;
  /** Switches `sessionId`'s plan in place and records it as the session's plan; throws once that session is no longer the open one. */
  switchPlan: (sessionId: string, plan: AutomationsPlanKey) => Promise<void>;
  /** Whether `sessionId` is the open session and holds `plan` right now, read fresh rather than from the last render. */
  holdsPlan: (sessionId: string, plan: AutomationsPlanKey) => boolean;
  failed: boolean;
  /** Mints a fresh session for the current selection. */
  retry: () => void;
}

type MintedSession = AutomationsCheckoutSessionResult & {
  plan: AutomationsPlanKey;
};
type OpenSession = Extract<MintedSession, { kind: "session" }>;

/** The one meaning of "`sessionId` is still the open session" for every read and write below. */
function isOpenSession(
  entry: MintedSession | undefined,
  sessionId: string
): entry is OpenSession {
  return entry?.kind === "session" && entry.sessionId === sessionId;
}

/**
 * One Stripe checkout session per checkout visit, minted with the first
 * selection; a tier or cycle change never mints again; `switchPlan` updates
 * the open session instead, so the payment form never remounts.
 */
export function useAutomationsCheckoutSession({
  artistId,
  selection,
  returnTo,
  surface,
  enabled,
}: UseAutomationsCheckoutSessionOptions): UseAutomationsCheckoutSessionResult {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: automationsKeys.checkoutSession(artistId, returnTo),
    queryFn: async (): Promise<MintedSession> => {
      const result = await fetchAutomationsCheckoutSession({
        artistId,
        ...selection,
        returnTo,
        surface,
      });
      if (result.kind === "existing") {
        void invalidateAutomations(queryClient, {
          artistId,
          scope: ["entitlement"],
        });
      }
      return { ...result, plan: selection };
    },
    enabled,
    staleTime: SESSION_LIFETIME_MS,
    gcTime: SESSION_LIFETIME_MS,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  // Sessions live with the page: the next visit, or another admin signed in on this tab, mints its own.
  useEffect(
    () => () => removeAutomationsCheckoutSessions(queryClient, artistId),
    [artistId, queryClient]
  );

  const switchPlan = useCallback(
    async (sessionId: string, plan: AutomationsPlanKey) => {
      const key = automationsKeys.checkoutSession(artistId, returnTo);
      if (!isOpenSession(queryClient.getQueryData(key), sessionId)) {
        throw new Error("Checkout session is no longer open");
      }
      await updateAutomationsCheckoutPlan({ artistId, sessionId, ...plan });
      // A Retry may have minted a new session meanwhile; it keeps its own plan.
      queryClient.setQueryData<MintedSession>(key, (old) =>
        isOpenSession(old, sessionId) ? { ...old, plan } : old
      );
    },
    [artistId, queryClient, returnTo]
  );

  const holdsPlan = useCallback(
    (sessionId: string, plan: AutomationsPlanKey) => {
      const open = queryClient.getQueryData<MintedSession>(
        automationsKeys.checkoutSession(artistId, returnTo)
      );
      return isOpenSession(open, sessionId) && isSamePlan(open.plan, plan);
    },
    [artistId, queryClient, returnTo]
  );

  return {
    session: query.data?.kind === "session" ? query.data : null,
    switchPlan,
    holdsPlan,
    failed: query.isError,
    retry: () => void query.refetch({ cancelRefetch: false }),
  };
}
